import Box from '@mui/joy/Box'
import Stack from '@mui/joy/Stack'
import Typography from '@mui/joy/Typography'
import IconButton from '@mui/joy/IconButton'
import CircularProgress from '@mui/joy/CircularProgress'
import Tooltip from '@mui/joy/Tooltip'
import MergeTypeIcon from '@mui/icons-material/MergeType'
import { CrmCompany } from 'api/oracleApi'

interface Props {
    companies: CrmCompany[]
    loading: boolean
    onSelect: (company: CrmCompany) => void
    onMerge: (company: CrmCompany) => void
    selected: CrmCompany | null
    reviewMode?: boolean
}

function relativeDate(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime()
    const days = Math.floor(diff / 86400000)
    if (days === 0) return 'today'
    if (days === 1) return '1d ago'
    if (days < 30) return `${days}d ago`
    const months = Math.floor(days / 30)
    return `${months}mo ago`
}

const ROW_HEIGHT = 44

export function CompaniesTable({ companies, loading, onSelect, onMerge, selected, reviewMode = false }: Props) {
    if (loading) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                <CircularProgress size="sm" />
            </Stack>
        )
    }

    if (companies.length === 0) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                <Typography level="body-sm" color="neutral" sx={{ fontFamily: 'monospace' }}>
                    — no records —
                </Typography>
            </Stack>
        )
    }

    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 'sm',
                overflow: 'hidden',
                fontFamily: 'monospace',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 72px 80px',
                    px: 1.5,
                    py: 0.75,
                    bgcolor: 'background.level3',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                }}
            >
                <Typography
                    level="body-xs"
                    sx={{ fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 10, color: 'text.tertiary' }}
                >
                    {reviewMode ? `company · ${companies.length} pending review` : `company · ${companies.length} total`}
                </Typography>
                <Typography
                    level="body-xs"
                    sx={{
                        fontFamily: 'monospace',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        fontSize: 10,
                        color: 'text.tertiary',
                        textAlign: 'right',
                    }}
                >
                    added
                </Typography>
                <Typography
                    level="body-xs"
                    sx={{
                        fontFamily: 'monospace',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        fontSize: 10,
                        color: 'text.tertiary',
                        textAlign: 'right',
                        pr: 0.5,
                    }}
                >
                    actions
                </Typography>
            </Box>

            {/* Rows */}
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {companies.map((c) => {
                    const isSelected = selected?.id === c.id
                    return (
                        <Box
                            key={c.id}
                            onClick={() => onSelect(c)}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 72px 80px',
                                alignItems: 'center',
                                height: ROW_HEIGHT,
                                px: 1.5,
                                cursor: 'pointer',
                                position: 'relative',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                bgcolor: isSelected ? 'primary.softBg' : 'background.surface',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: 3,
                                    bgcolor: isSelected ? 'primary.500' : reviewMode ? 'warning.400' : 'transparent',
                                    transition: 'background-color 0.1s',
                                },
                                '&:hover': {
                                    bgcolor: isSelected ? 'primary.softBg' : 'background.level1',
                                },
                                '&:last-child': { borderBottom: 'none' },
                            }}
                        >
                            {/* Name + ID */}
                            <Stack gap={0} sx={{ overflow: 'hidden', pl: 0.5 }}>
                                <Typography
                                    level="body-sm"
                                    fontWeight="md"
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        color: isSelected ? 'primary.700' : 'text.primary',
                                        fontSize: 13,
                                    }}
                                >
                                    {c.name_display}
                                </Typography>
                                <Typography
                                    level="body-xs"
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: 10,
                                        color: 'text.tertiary',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {c.id.slice(0, 20)}
                                </Typography>
                            </Stack>

                            {/* Date */}
                            <Typography level="body-xs" sx={{ fontFamily: 'monospace', fontSize: 10, color: 'text.tertiary', textAlign: 'right' }}>
                                {relativeDate(c.created_at)}
                            </Typography>

                            {/* Actions */}
                            <Stack direction="row" justifyContent="flex-end" gap={0.25} sx={{ pr: 0.25 }}>
                                <Tooltip title="Merge into another company" placement="top">
                                    <IconButton
                                        size="sm"
                                        variant="plain"
                                        color="neutral"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            onMerge(c)
                                        }}
                                        sx={{
                                            '--IconButton-size': '28px',
                                            color: 'text.tertiary',
                                            '&:hover': { color: 'warning.500', bgcolor: 'warning.softBg' },
                                        }}
                                    >
                                        <MergeTypeIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Box>
                    )
                })}
            </Box>
        </Box>
    )
}
